import { FormInputType } from '@doss/prisma/types'
import { z } from 'zod'
import { defineSystemEvent } from './definition.js'
import { systemEventChangesSchema, systemEventFailureDataSchema } from './primitives.js'

// Display names and kinds ride along where the writer has them, as on table events.
const formData = z.strictObject({
  form_id: z.uuid(),
  generation: z.enum(['v1', 'v2']).optional(),
  name: z.string().optional(),
})
const formTarget = formData.pick({ form_id: true, generation: true })
const inputData = formTarget.extend({
  form_input_id: z.uuid(),
  label: z.string().optional(),
  input_type: z.enum(FormInputType).optional(),
})
// Successful comment operations know the workspace. Failures may happen before it is resolved.
// Row comments also retain table_id for authorization and row_id for object history.
const commentScope = z
  .strictObject({
    workspace_id: z.uuid().optional(),
    table_id: z.uuid().optional(),
    row_id: z.uuid().optional(),
    form_id: z.uuid().optional(),
  })
  .refine(
    (data) => data.row_id === undefined || data.table_id !== undefined,
    'A row comment requires table_id',
  )
const commentTarget = commentScope.safeExtend({ comment_id: z.uuid() })
const commentData = commentTarget.safeExtend({ workspace_id: z.uuid() })
const buttonData = z.strictObject({
  button_id: z.uuid(),
  workspace_id: z.uuid().optional(),
  table_id: z.uuid().optional(),
  form_id: z.uuid().optional(),
  row_id: z.uuid().optional(),
})
const chartData = z.strictObject({
  chart_id: z.uuid(),
  table_id: z.uuid(),
  name: z.string().optional(),
})
const chartTarget = chartData.pick({ chart_id: true, table_id: true })

export const interactionSystemEvents = {
  formCreateSuccess: defineSystemEvent({
    type: 'CREATE_FORM',
    status: 'SUCCESS',
    entityTypes: ['FORM'],
    data: formData,
  }),
  formCreateFailed: defineSystemEvent({
    type: 'CREATE_FORM',
    status: 'FAIL',
    entityTypes: ['FORM'],
    data: systemEventFailureDataSchema.extend({ name: z.string().optional() }),
  }),
  formUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_FORM',
    status: 'SUCCESS',
    entityTypes: ['FORM'],
    data: formData.extend({ changes: systemEventChangesSchema }),
  }),
  formUpdateFailed: defineSystemEvent({
    type: 'UPDATE_FORM',
    status: 'FAIL',
    entityTypes: ['FORM'],
    data: formData.extend(systemEventFailureDataSchema.shape),
  }),
  formDeleteSuccess: defineSystemEvent({
    type: 'DELETE_FORM',
    status: 'SUCCESS',
    entityTypes: ['FORM'],
    data: formData,
  }),
  formDeleteFailed: defineSystemEvent({
    type: 'DELETE_FORM',
    status: 'FAIL',
    entityTypes: ['FORM'],
    data: formData.extend(systemEventFailureDataSchema.shape),
  }),
  formSubmitSuccess: defineSystemEvent({
    type: 'SUBMIT_FORM',
    status: 'SUCCESS',
    entityTypes: ['FORM'],
    data: formData,
  }),
  formSubmitFailed: defineSystemEvent({
    type: 'SUBMIT_FORM',
    status: 'FAIL',
    entityTypes: ['FORM'],
    data: formData.extend(systemEventFailureDataSchema.shape),
  }),
  formInputCreateSuccess: defineSystemEvent({
    type: 'CREATE_FORM_INPUT',
    status: 'SUCCESS',
    entityTypes: ['FORM_INPUT'],
    data: inputData,
  }),
  formInputCreateFailed: defineSystemEvent({
    type: 'CREATE_FORM_INPUT',
    status: 'FAIL',
    entityTypes: ['FORM_INPUT', 'FORM'],
    // The input that was asked for, when the request named it.
    data: inputData.omit({ form_input_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  formInputUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_FORM_INPUT',
    status: 'SUCCESS',
    entityTypes: ['FORM_INPUT'],
    data: inputData.extend({ changes: systemEventChangesSchema }),
  }),
  formInputUpdateFailed: defineSystemEvent({
    type: 'UPDATE_FORM_INPUT',
    status: 'FAIL',
    entityTypes: ['FORM_INPUT'],
    data: inputData.extend(systemEventFailureDataSchema.shape),
  }),
  formInputDeleteSuccess: defineSystemEvent({
    type: 'DELETE_FORM_INPUT',
    status: 'SUCCESS',
    entityTypes: ['FORM_INPUT'],
    data: inputData,
  }),
  formInputDeleteFailed: defineSystemEvent({
    type: 'DELETE_FORM_INPUT',
    status: 'FAIL',
    entityTypes: ['FORM_INPUT'],
    data: inputData.extend(systemEventFailureDataSchema.shape),
  }),
  commentCreateSuccess: defineSystemEvent({
    type: 'CREATE_COMMENT',
    status: 'SUCCESS',
    entityTypes: ['COMMENT'],
    data: commentData,
  }),
  commentCreateFailed: defineSystemEvent({
    type: 'CREATE_COMMENT',
    status: 'FAIL',
    entityTypes: ['COMMENT', 'TABLE_ROW', 'TABLE', 'FORM'],
    data: commentScope.safeExtend(systemEventFailureDataSchema.shape),
  }),
  commentDeleteSuccess: defineSystemEvent({
    type: 'DELETE_COMMENT',
    status: 'SUCCESS',
    entityTypes: ['COMMENT'],
    data: commentData,
  }),
  commentDeleteFailed: defineSystemEvent({
    type: 'DELETE_COMMENT',
    status: 'FAIL',
    entityTypes: ['COMMENT'],
    data: commentTarget.safeExtend(systemEventFailureDataSchema.shape),
  }),
  buttonClickSuccess: defineSystemEvent({
    type: 'CLICK_BUTTON',
    status: 'SUCCESS',
    entityTypes: ['BUTTON'],
    data: buttonData,
  }),
  buttonClickFailed: defineSystemEvent({
    type: 'CLICK_BUTTON',
    status: 'FAIL',
    entityTypes: ['BUTTON'],
    data: buttonData.extend(systemEventFailureDataSchema.shape),
  }),
  chartCreateSuccess: defineSystemEvent({
    type: 'CREATE_CHART',
    status: 'SUCCESS',
    entityTypes: ['CHART'],
    data: chartData,
  }),
  chartCreateFailed: defineSystemEvent({
    type: 'CREATE_CHART',
    status: 'FAIL',
    entityTypes: ['CHART'],
    // Validation can fail before the chart's table is resolved.
    data: systemEventFailureDataSchema.extend({ table_id: z.uuid().optional() }),
  }),
  chartUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_CHART',
    status: 'SUCCESS',
    entityTypes: ['CHART'],
    data: chartTarget.extend({ changes: systemEventChangesSchema }),
  }),
  chartUpdateFailed: defineSystemEvent({
    type: 'UPDATE_CHART',
    status: 'FAIL',
    entityTypes: ['CHART'],
    data: chartTarget.extend(systemEventFailureDataSchema.shape),
  }),
  chartDeleteSuccess: defineSystemEvent({
    type: 'DELETE_CHART',
    status: 'SUCCESS',
    entityTypes: ['CHART'],
    data: chartData,
  }),
  chartDeleteFailed: defineSystemEvent({
    type: 'DELETE_CHART',
    status: 'FAIL',
    entityTypes: ['CHART'],
    data: chartTarget.extend(systemEventFailureDataSchema.shape),
  }),
}
